// Per-user admin quick menu link visibility — London Smoke & Cure
// Stored on Firestore users/{uid}.adminNavLinkVisibility

import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import {
    ADMIN_NAV_LINKS,
    PINNED_ADMIN_NAV_IDS,
} from '../components/admin/adminNavConfig';

const STORAGE_KEY_PREFIX = 'lsc_admin_nav_visibility_';

/**
 * @typedef {Record<string, boolean>} AdminNavLinkVisibility
 */

/**
 * @returns {AdminNavLinkVisibility}
 */
export function defaultAdminNavLinkVisibility() {
    /** @type {AdminNavLinkVisibility} */
    const vis = {};
    for (const link of ADMIN_NAV_LINKS) {
        vis[link.id] = true;
    }
    return vis;
}

/**
 * @param {unknown} raw
 * @returns {AdminNavLinkVisibility}
 */
export function normalizeAdminNavLinkVisibility(raw) {
    const base = defaultAdminNavLinkVisibility();
    if (!raw || typeof raw !== 'object') return base;

    for (const link of ADMIN_NAV_LINKS) {
        if (Object.prototype.hasOwnProperty.call(raw, link.id)) {
            base[link.id] = raw[link.id] !== false;
        }
    }

    for (const pinnedId of PINNED_ADMIN_NAV_IDS) {
        base[pinnedId] = true;
    }

    return base;
}

/**
 * @param {AdminNavLinkVisibility} visibility
 * @param {string} linkId
 * @returns {boolean}
 */
export function isAdminNavLinkVisible(visibility, linkId) {
    if (PINNED_ADMIN_NAV_IDS.has(linkId)) return true;
    return visibility?.[linkId] !== false;
}

/**
 * @param {AdminNavLinkVisibility} visibility
 * @param {string} linkId
 * @param {boolean} visible
 * @returns {AdminNavLinkVisibility}
 */
export function setAdminNavLinkVisible(visibility, linkId, visible) {
    if (PINNED_ADMIN_NAV_IDS.has(linkId)) {
        return { ...visibility, [linkId]: true };
    }
    return { ...visibility, [linkId]: visible };
}

function cacheKey(uid) {
    return `${STORAGE_KEY_PREFIX}${uid}`;
}

/**
 * @param {string} uid
 * @returns {AdminNavLinkVisibility | null}
 */
export function readCachedAdminNavVisibility(uid) {
    if (!uid || typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(cacheKey(uid));
        if (!raw) return null;
        return normalizeAdminNavLinkVisibility(JSON.parse(raw));
    } catch {
        return null;
    }
}

/**
 * @param {string} uid
 * @param {AdminNavLinkVisibility} visibility
 */
export function writeCachedAdminNavVisibility(uid, visibility) {
    if (!uid || typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(cacheKey(uid), JSON.stringify(visibility));
    } catch {
        /* ignore quota */
    }
}

/**
 * @param {string} uid
 * @returns {Promise<AdminNavLinkVisibility>}
 */
export async function loadAdminNavLinkVisibility(uid) {
    if (!uid) return defaultAdminNavLinkVisibility();

    const cached = readCachedAdminNavVisibility(uid);
    if (cached) {
        // background refresh
        getDoc(doc(db, 'users', uid))
            .then((snap) => {
                if (snap.exists()) {
                    const vis = normalizeAdminNavLinkVisibility(
                        snap.data()?.adminNavLinkVisibility
                    );
                    writeCachedAdminNavVisibility(uid, vis);
                }
            })
            .catch(() => {});
        return cached;
    }

    try {
        const snap = await getDoc(doc(db, 'users', uid));
        if (!snap.exists()) return defaultAdminNavLinkVisibility();
        const vis = normalizeAdminNavLinkVisibility(snap.data()?.adminNavLinkVisibility);
        writeCachedAdminNavVisibility(uid, vis);
        return vis;
    } catch (e) {
        console.error('[adminNavPreferencesService] load', e);
        return defaultAdminNavLinkVisibility();
    }
}

/**
 * @param {string} uid
 * @param {AdminNavLinkVisibility} visibility
 */
export async function saveAdminNavLinkVisibility(uid, visibility) {
    if (!uid) return;
    const normalized = normalizeAdminNavLinkVisibility(visibility);
    writeCachedAdminNavVisibility(uid, normalized);
    try {
        await updateDoc(doc(db, 'users', uid), {
            adminNavLinkVisibility: normalized,
        });
    } catch (e) {
        console.error('[adminNavPreferencesService] save', e);
        throw e;
    }
}
